import { z } from 'zod';

import { reasonSchema } from './clinical-common.ts';
import { amountSchema } from './money.ts';
import { stockQuantitySchema } from './stock.ts';

// Stock items, balances and documents on the wire (docs/plans/phase1-stock.md §3.3).
// Quantities are whole base units. Money is integer cents in a string (ADR 0006). Dates
// on the calendar (expiry) are `YYYY-MM-DD`; instants are ISO strings in UTC.

/**
 * Operational windows (plan §12, decision 5). Provisional defaults, not clinical
 * thresholds; they become tenant settings when the settings screen for stock lands.
 */
export const STOCK_DEFAULTS = {
  nearExpiryDays: 90,
  shortExpiryOnReceiptDays: 180,
} as const;

const unitCode = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Lower-case letters, digits and _');
const display = z.string().trim().min(1).max(60);
const isoDay = z.iso.date();
const optionalNote = z.string().trim().max(500).optional();

// --- Stock items -------------------------------------------------------------------------

export const packUnitSchema = z.object({
  code: unitCode,
  display,
  baseUnitsPerPack: stockQuantitySchema,
  status: z.enum(['active', 'inactive']),
});

export const stockItemSchema = z.object({
  itemId: z.uuid(),
  code: z.string(),
  name: z.string(),
  category: z.string(),
  status: z.enum(['active', 'inactive']),
  /** Null until someone with stock.manage sets the base unit and packs. */
  stock: z
    .object({
      baseUnit: z.object({ code: z.string(), display: z.string() }),
      expiryTracked: z.boolean(),
      isControlled: z.boolean(),
      packUnits: z.array(packUnitSchema),
    })
    .nullable(),
});

export type StockItemView = z.infer<typeof stockItemSchema>;

export const stockItemSearchBodySchema = z.object({
  query: z.string().trim().max(100).default(''),
  limit: z.number().int().min(1).max(50).default(20),
});

export const setStockDetailsBodySchema = z.object({
  baseUnitCode: unitCode,
  baseUnitDisplay: display,
  expiryTracked: z.boolean(),
  isControlled: z.boolean(),
  /** Packs not listed are switched off, never deleted: old GRN lines still name them. */
  packUnits: z
    .array(packUnitSchema.omit({ status: true }))
    .max(10)
    .refine((packs) => new Set(packs.map((p) => p.code)).size === packs.length, {
      message: 'Each pack has its own code',
    }),
});

// --- Balances -----------------------------------------------------------------------------

export const EXPIRY_STATUSES = ['expired', 'near_expiry', 'ok', 'no_expiry'] as const;
export const REORDER_STATUSES = [
  'out_of_stock',
  'at_or_below_reorder_level',
  'ok',
  'not_set',
] as const;

export const batchBalanceSchema = z.object({
  batchId: z.uuid(),
  batchNumber: z.string(),
  expiryDate: isoDay.nullable(),
  expiryStatus: z.enum(EXPIRY_STATUSES),
  daysToExpiry: z.number().int().nullable(),
  quantity: z.number().int(),
  /** Position in FEFO order among usable batches, 1 = next to issue; null if not usable. */
  fefoRank: z.number().int().nullable(),
});

export const stockOnHandRowSchema = z.object({
  itemId: z.uuid(),
  code: z.string(),
  name: z.string(),
  baseUnitDisplay: z.string(),
  usable: z.number().int(),
  expired: z.number().int(),
  earliestUsableExpiry: isoDay.nullable(),
  reorderLevel: z.number().int().nullable(),
  reorderStatus: z.enum(REORDER_STATUSES),
});

export type StockOnHandRow = z.infer<typeof stockOnHandRowSchema>;

export const storeItemDetailSchema = z.object({
  item: stockItemSchema,
  storeId: z.uuid(),
  batches: z.array(batchBalanceSchema),
  usable: z.number().int(),
  reorderLevel: z.number().int().nullable(),
  maxLevel: z.number().int().nullable(),
  reorderStatus: z.enum(REORDER_STATUSES),
});

export const setLevelsBodySchema = z
  .object({
    reorderLevel: z.number().int().min(0).max(2_147_483_647),
    maxLevel: z.number().int().min(0).max(2_147_483_647).nullable(),
  })
  .refine((v) => v.maxLevel === null || v.maxLevel >= v.reorderLevel, {
    message: 'The maximum can’t be below the reorder level',
    path: ['maxLevel'],
  });

export const MOVEMENT_KIND_LIST = [
  'opening_balance',
  'receipt',
  'receipt_reversal',
  'transfer_out',
  'transfer_in',
  'adjustment',
  'count_variance',
  'dispense',
  'dispense_return',
  'sale',
  'sale_return',
  'issue',
] as const;

export const stockCardRowSchema = z.object({
  movementId: z.uuid(),
  occurredAt: z.iso.datetime({ offset: true }),
  recordedAt: z.iso.datetime({ offset: true }),
  kind: z.enum(MOVEMENT_KIND_LIST),
  batchId: z.uuid(),
  batchNumber: z.string(),
  quantity: z.number().int(),
  /** The ledger's running sum for this item in this store, after this movement. */
  balanceAfter: z.number().int(),
  /** The document's human number, e.g. F1-GRN000012. */
  documentNumber: z.string().nullable(),
  recordedBy: z.string(),
});

export const expiringRowSchema = z.object({
  itemId: z.uuid(),
  name: z.string(),
  baseUnitDisplay: z.string(),
  batchId: z.uuid(),
  batchNumber: z.string(),
  expiryDate: isoDay,
  status: z.enum(['expired', 'near_expiry']),
  daysToExpiry: z.number().int(),
  quantity: z.number().int(),
});

export const reorderRowSchema = stockOnHandRowSchema;

export const fefoPreviewBodySchema = z.object({
  itemId: z.uuid(),
  quantity: stockQuantitySchema,
});

export const allocationSchema = z.object({ batchId: z.uuid(), quantity: stockQuantitySchema });

export const fefoPreviewSchema = z.object({
  allocations: z.array(
    allocationSchema.extend({ batchNumber: z.string(), expiryDate: isoDay.nullable() }),
  ),
});

// --- Goods received notes -----------------------------------------------------------------

export const grnDraftLineSchema = z.object({
  itemId: z.uuid(),
  batchNumber: z.string().trim().min(1).max(64),
  /** Null only for items not expiry-tracked. The client parses what is printed on the pack. */
  expiryDate: isoDay.nullable(),
  packUnitCode: unitCode,
  packs: stockQuantitySchema,
  packCost: amountSchema,
  /** The LPO line this receives against, on a GRN made from an LPO (procurement plan §2.6). */
  purchaseOrderLineId: z.uuid().optional(),
});

export type GrnDraftLine = z.infer<typeof grnDraftLineSchema>;

const grnFields = {
  supplierId: z.uuid(),
  supplierDocumentNumber: z.string().trim().max(60).optional(),
  receivedAt: z.iso.datetime({ offset: true }),
  note: optionalNote,
  lines: z.array(grnDraftLineSchema).max(200),
};

export const createGrnBodySchema = z.object({
  clientRequestId: z.uuid(),
  storeId: z.uuid(),
  /** Receiving against an approved LPO: its supplier and store must match. */
  purchaseOrderId: z.uuid().optional(),
  ...grnFields,
});

export const updateGrnBodySchema = z.object({
  expectedVersion: z.number().int().min(1),
  ...grnFields,
});

export const postGrnBodySchema = z.object({
  expectedVersion: z.number().int().min(1),
  /** Reasons for accepting short-dated lines, by line number (1-based). */
  shortExpiryReasons: z.record(z.string().regex(/^[1-9]\d{0,2}$/), reasonSchema).default({}),
  /** Reasons for receiving more than the LPO line ordered, by line number (1-based). */
  overReceiptReasons: z.record(z.string().regex(/^[1-9]\d{0,2}$/), reasonSchema).default({}),
});

export const grnLineViewSchema = z.object({
  lineNo: z.number().int(),
  itemId: z.uuid(),
  itemName: z.string(),
  batchNumber: z.string(),
  expiryDate: isoDay.nullable(),
  packUnitCode: z.string(),
  packUnitDisplay: z.string(),
  baseUnitsPerPack: z.number().int(),
  packs: z.number().int(),
  quantity: z.number().int(),
  packCost: amountSchema,
  lineCost: amountSchema,
  shortExpiryReason: z.string().nullable(),
  purchaseOrderLineId: z.uuid().nullable(),
  overReceiptReason: z.string().nullable(),
});

export const grnSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  storeId: z.uuid(),
  supplierId: z.uuid(),
  supplierName: z.string(),
  supplierDocumentNumber: z.string().nullable(),
  receivedAt: z.iso.datetime({ offset: true }),
  status: z.enum(['draft', 'posted']),
  version: z.number().int(),
  note: z.string().nullable(),
  lines: z.array(grnLineViewSchema),
  total: amountSchema,
  postedAt: z.iso.datetime({ offset: true }).nullable(),
  purchaseOrder: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  /** The latest reversal request, if any: requested, approved, rejected or cancelled. */
  reversal: z
    .object({
      id: z.uuid(),
      status: z.enum(['requested', 'approved', 'rejected', 'cancelled']),
      reason: z.string(),
    })
    .nullable(),
});

export type GrnView = z.infer<typeof grnSchema>;

export const grnListQuerySchema = z.object({
  storeId: z.uuid(),
  status: z.enum(['draft', 'posted']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});

export const grnSummarySchema = grnSchema
  .omit({ lines: true })
  .extend({ lineCount: z.number().int() });

// --- Transfers ----------------------------------------------------------------------------

export const transferLineBodySchema = z
  .object({
    itemId: z.uuid(),
    quantity: stockQuantitySchema,
    /** Omitted: the server picks FEFO. Given and different from FEFO: a reason is required. */
    batches: z.array(allocationSchema).min(1).max(20).optional(),
    overrideReason: reasonSchema.optional(),
  })
  .refine((l) => l.batches === undefined || l.overrideReason === undefined || l.batches.length > 0);

export const createTransferBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    fromStoreId: z.uuid(),
    toStoreId: z.uuid(),
    note: optionalNote,
    lines: z.array(transferLineBodySchema).min(1).max(100),
  })
  .refine((t) => t.fromStoreId !== t.toStoreId, {
    message: 'Choose two different stores',
    path: ['toStoreId'],
  })
  .refine((t) => new Set(t.lines.map((l) => l.itemId)).size === t.lines.length, {
    message: 'List each item once',
    path: ['lines'],
  });

export const transferSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  fromStoreId: z.uuid(),
  toStoreId: z.uuid(),
  transferredAt: z.iso.datetime({ offset: true }),
  note: z.string().nullable(),
  lines: z.array(
    z.object({
      lineNo: z.number().int(),
      itemId: z.uuid(),
      itemName: z.string(),
      batchId: z.uuid(),
      batchNumber: z.string(),
      quantity: z.number().int(),
      batchOverrideReason: z.string().nullable(),
    }),
  ),
});

// --- Adjustments and opening balances -----------------------------------------------------

export const ADJUSTMENT_REASONS = [
  'damaged',
  'expired',
  'lost',
  'theft_suspected',
  'found',
  'data_correction',
  'other',
] as const;

/** A batch the store holds, or (opening balances and found stock) a new one. */
const batchRefSchema = z.union([
  z.object({ batchId: z.uuid() }),
  z.object({
    newBatch: z.object({
      batchNumber: z.string().trim().min(1).max(64),
      expiryDate: isoDay.nullable(),
    }),
  }),
]);

export const adjustmentLineBodySchema = z.object({
  itemId: z.uuid(),
  batch: batchRefSchema,
  /** Signed base units: negative removes stock. Opening balances are positive. */
  quantity: z
    .number()
    .int()
    .min(-2_147_483_647)
    .max(2_147_483_647)
    .refine((q) => q !== 0, 'A quantity can’t be zero'),
});

export const createAdjustmentBodySchema = z
  .object({
    clientRequestId: z.uuid(),
    storeId: z.uuid(),
    kind: z.enum(['adjustment', 'opening_balance']),
    /** Required for adjustments; opening balances carry their own reason. */
    reasonCode: z.enum(ADJUSTMENT_REASONS).optional(),
    note: optionalNote,
    occurredAt: z.iso.datetime({ offset: true }),
    lines: z.array(adjustmentLineBodySchema).min(1).max(500),
  })
  .refine((a) => (a.kind === 'adjustment') === (a.reasonCode !== undefined), {
    message: 'An adjustment needs a reason; an opening balance doesn’t take one',
    path: ['reasonCode'],
  })
  .refine(
    (a) =>
      a.reasonCode === undefined ||
      !['other', 'theft_suspected'].includes(a.reasonCode) ||
      (a.note !== undefined && a.note.length > 0),
    { message: 'Add a note saying what happened', path: ['note'] },
  )
  .refine((a) => a.kind === 'adjustment' || a.lines.every((l) => l.quantity > 0), {
    message: 'Opening balances add stock',
    path: ['lines'],
  });

export const decisionBodySchema = z.object({ reason: reasonSchema });

export const adjustmentSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  storeId: z.uuid(),
  kind: z.enum(['adjustment', 'opening_balance']),
  reasonCode: z.enum([...ADJUSTMENT_REASONS, 'opening_balance']),
  status: z.enum(['requested', 'approved', 'rejected', 'cancelled']),
  note: z.string().nullable(),
  occurredAt: z.iso.datetime({ offset: true }),
  requestedBy: z.object({ id: z.uuid(), name: z.string() }),
  decidedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  decidedAt: z.iso.datetime({ offset: true }).nullable(),
  decisionReason: z.string().nullable(),
  /** True when the caller may approve or reject it (holds the permission, isn't the requester). */
  canDecide: z.boolean(),
  lines: z.array(
    z.object({
      lineNo: z.number().int(),
      itemId: z.uuid(),
      itemName: z.string(),
      batchId: z.uuid(),
      batchNumber: z.string(),
      expiryDate: isoDay.nullable(),
      quantity: z.number().int(),
    }),
  ),
});

export type AdjustmentView = z.infer<typeof adjustmentSchema>;

export const adjustmentListQuerySchema = z.object({
  storeId: z.uuid().optional(),
  status: z.enum(['requested', 'approved', 'rejected', 'cancelled']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

// --- Counts -------------------------------------------------------------------------------

export const createCountBodySchema = z.object({
  clientRequestId: z.uuid(),
  storeId: z.uuid(),
  note: optionalNote,
});

export const countLineBodySchema = z.object({
  itemId: z.uuid(),
  batch: batchRefSchema,
  countedQuantity: z.number().int().min(0).max(2_147_483_647),
});

export const approveCountBodySchema = z.object({
  reason: reasonSchema,
  /** A reason code for every line whose count differs from the ledger, by batch id. */
  varianceReasons: z.record(
    z.uuid(),
    z.object({ reasonCode: z.enum(ADJUSTMENT_REASONS), note: optionalNote }),
  ),
});

export const countSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  storeId: z.uuid(),
  status: z.enum(['in_progress', 'submitted', 'approved', 'rejected', 'cancelled']),
  blind: z.boolean(),
  startedAt: z.iso.datetime({ offset: true }),
  note: z.string().nullable(),
  canDecide: z.boolean(),
  decisionReason: z.string().nullable(),
  /** Batches holding stock in the store that haven't been counted yet (the count sheet). */
  toCount: z.array(
    z.object({
      itemId: z.uuid(),
      itemName: z.string(),
      baseUnitDisplay: z.string(),
      batchId: z.uuid(),
      batchNumber: z.string(),
      expiryDate: isoDay.nullable(),
    }),
  ),
  lines: z.array(
    z.object({
      itemId: z.uuid(),
      itemName: z.string(),
      baseUnitDisplay: z.string(),
      batchId: z.uuid(),
      batchNumber: z.string(),
      expiryDate: isoDay.nullable(),
      countedQuantity: z.number().int(),
      countedAt: z.iso.datetime({ offset: true }),
      /** Hidden while a blind count is in progress. */
      systemQuantity: z.number().int().nullable(),
      variance: z.number().int().nullable(),
      varianceReasonCode: z.enum([...ADJUSTMENT_REASONS, 'opening_balance']).nullable(),
    }),
  ),
});

export type CountView = z.infer<typeof countSchema>;

export const countSummarySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  status: z.enum(['in_progress', 'submitted', 'approved', 'rejected', 'cancelled']),
  startedAt: z.iso.datetime({ offset: true }),
  canDecide: z.boolean(),
});
